import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fetchClergy } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Search, ChevronDown, ChevronRight, Church, Phone, Mail, Award, MapPin, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SeoHead from '@/components/Seo';

const Clergy = () => {
  const [clergy, setClergy] = useState([]);
  const [filteredClergy, setFilteredClergy] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState('hierarchy');
  const [expandedGroups, setExpandedGroups] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    const loadClergy = async () => {
      try {
        const response = await fetchClergy();
        const transformed = response.data.priests.map((item) => {
          const primaryParish = item.parishes?.[0];
          return {
            id: item.id,
            name: item.name,
            title: item.title,
            slug: item.slug,
            archdeaconry: primaryParish?.archdeaconry?.name ?? 'Diocesan Headquarters',
            parish: primaryParish?.name ?? 'Diocese',
            parish_location: primaryParish?.location ?? '',
            email: item.email,
            phone: item.phone,
            rank: item.assignments?.[0]?.role ?? '',
            hierarchy: item.hierarchy ?? 999,
            photo: item.photo ?? 'https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png',
          };
        });
        setClergy(transformed);
        setFilteredClergy(transformed);
      } catch (error) {
        toast({ title: "Error", description: "Could not load clergy directory.", variant: "destructive" });
      }
    };
    loadClergy();
  }, [toast]);

  const seoContent = (
    <SeoHead
      title="Clergy Directory | Ibadan South Diocese"
      description="Find and connect with the ordained ministers of the Ibadan South Diocese. Search by name, parish, or view our interactive map of clergy locations."
      ogImage="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/directory_og.jpg" 
    />
  );

  useEffect(() => {
    let filtered = clergy.filter(member =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.parish.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (groupBy === 'hierarchy') {
      filtered.sort((a, b) => (a.hierarchy ?? 999) - (b.hierarchy ?? 999));
    }
    setFilteredClergy(filtered);
  }, [clergy, searchTerm, groupBy]);

  const groupedByArch = filteredClergy.reduce((acc, member) => {
    if (!acc[member.archdeaconry]) acc[member.archdeaconry] = [];
    acc[member.archdeaconry].push(member);
    return acc;
  }, {});

  const toggleExpand = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const ClergyCard = ({ member }) => (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="group"
    >
      <Card className="relative overflow-hidden border-none shadow-sm hover:shadow-2xl transition-all duration-500 bg-white rounded-[2rem]">
        {/* Photo with Gradient Overlay */}
        <div className="aspect-[4/5] relative overflow-hidden">
          <img
            src={member.photo}
            alt={member.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

          {/* Rank Badge */}
          <div className="absolute top-4 left-4">
            <span className="bg-amber-600/50 backdrop-blur-md border border-white/20 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
              {member.title}
            </span>
          </div>

          {/* Floating Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <Link to={`/clergy/${member.slug}`}>
              <h3 className="text-xl font-serif font-bold leading-tight group-hover:text-amber-400 transition-colors">
                {member.name}
              </h3>
            </Link>

            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 text-xs text-white/70">
                <Church className="h-3 w-3 text-amber-500" />
                <span className="truncate">{member.parish}</span>
              </div>
              {member.parish_location && (
                <div className="flex items-center gap-2 text-xs text-white/50">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{member.parish_location}</span>
                </div>
              )}
            </div>

            {/* Actions Bar */}
            <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
              <div className="flex gap-4">
                {member.phone && (
                  <a href={`tel:${member.phone}`} className="hover:text-amber-400 transition-colors">
                    <Phone size={14} />
                  </a>
                )}
                {member.email && (
                  <a href={`mailto:${member.email}`} className="hover:text-amber-400 transition-colors">
                    <Mail size={14} />
                  </a>
                )}
              </div>
              <Link to={`/clergy/${member.slug}`} className="text-[10px] font-black uppercase tracking-widest bg-amber-600 px-3 py-1.5 rounded-lg">
                Details
              </Link>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-[#FDFCFB] py-24">
      {seoContent}
      <div className="max-w-7xl mx-auto px-6">

        {/* Dynamic Header */}
        <div className="text-center mb-16">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center mb-4">
            <div className="bg-amber-100 text-amber-700 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.3em]">
              Laborers in the Vineyard
            </div>
          </motion.div>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 mb-6">Clergy <span className="text-amber-600">Directory</span></h1>

          {/* Integrated Search Bar */}
          <div className="max-w-3xl mx-auto mt-12 bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 p-2 flex flex-col md:flex-row items-center gap-2 border border-slate-100">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
              <Input
                placeholder="Search by name, parish, or location..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="border-none focus-visible:ring-0 pl-12 h-12 text-slate-600 placeholder:text-slate-300 rounded-full"
              />
            </div>
            <div className="h-8 w-px bg-slate-100 hidden md:block" />
            <Select value={groupBy} onValueChange={v => setGroupBy(v)}>
              <SelectTrigger className="w-full md:w-56 border-none focus:ring-0 h-12 font-bold text-xs uppercase tracking-widest text-slate-500">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hierarchy">Seniority Rank</SelectItem>
                <SelectItem value="archdeaconry">Archdeaconry</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid Display */}
        <AnimatePresence mode="popLayout">
          {groupBy === 'hierarchy' ? (
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
            >
              {filteredClergy.map(member => (
                <ClergyCard key={member.id} member={member} />
              ))}
            </motion.div>
          ) : (
            <div className="space-y-12">
              {Object.entries(groupedByArch).map(([arch, members]) => {
                const isOpen = expandedGroups[arch] ?? true;
                return (
                  <div key={arch} className="relative">
                    <button
                      className="w-full flex items-center justify-between py-6 border-b border-slate-200 group"
                      onClick={() => toggleExpand(arch)}
                    >
                      <div className="flex items-center gap-6">
                        <div className="bg-slate-900 text-white w-12 h-12 rounded-2xl flex items-center justify-center font-serif text-xl font-bold">
                          {members.length}
                        </div>
                        <div className="text-left">
                          <h2 className="text-2xl font-serif font-bold text-slate-900 group-hover:text-amber-600 transition-colors">
                            {arch} Archdeaconry
                          </h2>
                          <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Administrative Division</p>
                        </div>
                      </div>
                      <div className={`p-2 rounded-full transition-all ${isOpen ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                        {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </div>
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 pt-10 pb-16">
                            {members
                              .sort((a, b) => (a.hierarchy ?? 999) - (b.hierarchy ?? 999))
                              .map(member => (
                                <ClergyCard key={member.id} member={member} />
                              ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {filteredClergy.length === 0 && (
          <div className="text-center py-32 bg-white rounded-[3rem] border border-dashed border-slate-200">
            <Users className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <h3 className="text-xl font-serif text-slate-400 italic">No clergy found matching your search.</h3>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-amber-600 font-bold uppercase text-xs tracking-widest hover:underline"
            >
              Clear Search
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clergy;